import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { thumb } from "@/lib/media";
import { Status } from "./useApi";

const PAGE = 48;

const Orden = ({ m, onSave }) => {
  const [v, setV] = useState(String(m.orden || 0));
  useEffect(() => { setV(String(m.orden || 0)); }, [m.orden]);
  const save = () => { const n = Math.max(0, parseInt(v, 10) || 0); if (n !== (m.orden || 0)) onSave({ orden: n }); };
  return (
    <input className="gord" type="number" min="0" inputMode="numeric" value={v} aria-label="Número de orden" onChange={(e) => setV(e.target.value)}
      onBlur={save} onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }} data-testid={`gal-orden-${m.id}`} />
  );
};

const Card = ({ m, onPatch, onMove }) => (
  <div className={"gcardx" + (m.visible ? "" : " off")} data-testid={`gal-card-${m.id}`}>
    <a href={`https://drive.google.com/file/d/${m.id}/view`} target="_blank" rel="noopener noreferrer" className="gth" title="Abrir en Drive">
      <img src={thumb(m.id, 300)} alt="" loading="lazy" referrerPolicy="no-referrer" />
      <span className="gbadge">{m.tipo === "video" ? "Video" : "Foto"}</span>
      {m.destacado && <span className="gbadge gstar">Destacado</span>}
      {!m.visible && <span className="gbadge ghid">Oculto</span>}
    </a>
    <p className="gname" title={m.nombre}>{m.nombre}<br /><span className="note">{m.fecha || "Sin fecha"}</span></p>
    <div className="gacts">
      <button className="btn xbtn" onClick={() => onPatch({ visible: !m.visible })} data-testid={`gal-visible-${m.id}`}>{m.visible ? "Ocultar" : "Mostrar"}</button>
      <button className={"btn " + (m.destacado ? "btn-sol" : "xbtn")} onClick={() => onPatch({ destacado: !m.destacado })} aria-pressed={m.destacado} data-testid={`gal-destacar-${m.id}`}>{m.destacado ? "★ Destacado" : "☆ Destacar"}</button>
    </div>
    <div className="gacts">
      <button className="btn xbtn" onClick={() => onMove("subir")} aria-label="Subir" data-testid={`gal-subir-${m.id}`}>↑</button>
      <button className="btn xbtn" onClick={() => onMove("bajar")} aria-label="Bajar" data-testid={`gal-bajar-${m.id}`}>↓</button>
      <Orden m={m} onSave={onPatch} />
    </div>
  </div>
);

const Resumen = ({ r }) => (
  <p className="note" data-testid="gal-resumen">
    {r.total} archivos en la base · <b>{r.visibles} visibles</b> ({r.fotos} fotos, {r.videos} videos) · {r.ocultos} ocultos · {r.destacados} destacados en portada
  </p>
);

export const GaleriaView = () => {
  const [F, setF] = useState({ tipo: "", estado: "", q: "" });
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [n, setN] = useState(PAGE);
  const [syncing, setSyncing] = useState(false);

  const load = useCallback(async (limit) => {
    try {
      const r = await api.get("/media/admin", { params: { ...F, skip: 0, limit } });
      setData(r.data); setError("");
    } catch (e) { setError(errMsg(e)); }
  }, [F]);

  useEffect(() => { const t = setTimeout(() => load(n), 250); return () => clearTimeout(t); }, [load, n]);

  const patch = async (m, body) => {
    try { await api.patch(`/media/${m.id}`, body); await load(n); } catch (e) { toast.error(errMsg(e)); }
  };
  const move = async (m, dir) => {
    try { await api.post(`/media/${m.id}/mover`, { dir, tipo: F.tipo }); await load(n); } catch (e) { toast.error(errMsg(e)); }
  };
  const sync = async () => {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/media/sync");
      toast.success(r.nuevos ? `${r.nuevos} archivos nuevos agregados (${r.duplicadosOcultos} duplicados ocultos).` : "La galería ya estaba al día.");
      await load(n);
    } catch (e) { toast.error(errMsg(e)); } finally { setSyncing(false); }
  };
  const set = (k) => (e) => { setN(PAGE); setF({ ...F, [k]: e.target.value }); };

  return (
    <div data-testid="galeria-admin-view">
      <div className="panel">
        <h4>Galería pública</h4>
        <p className="note" style={{ marginTop: 0 }}>Los archivos se leen de la carpeta compartida de Drive. Oculta con un clic cualquier imagen sin autorización. Los destacados aparecen en la portada; el número de orden (1, 2, 3…) va primero y el resto sigue de más reciente a más antiguo.</p>
        {data && <Resumen r={data.resumen} />}
        <button className="btn btn-sol" onClick={sync} disabled={syncing} data-testid="gal-sync-btn">{syncing ? "Sincronizando…" : "Sincronizar galería"}</button>
      </div>
      <div className="filters" data-testid="gal-filters">
        <select value={F.tipo} onChange={set("tipo")} aria-label="Tipo" data-testid="gal-filter-tipo"><option value="">Fotos y videos</option><option value="foto">Fotos</option><option value="video">Videos</option></select>
        <select value={F.estado} onChange={set("estado")} aria-label="Estado" data-testid="gal-filter-estado"><option value="">Todos los estados</option><option value="visibles">Visibles</option><option value="ocultos">Ocultos</option><option value="destacados">Destacados</option></select>
        <input className="gq" value={F.q} onChange={set("q")} placeholder="Buscar por nombre de archivo" aria-label="Buscar" data-testid="gal-filter-q" />
      </div>
      {!data ? <Status data={data} error={error} /> : (
        <>
          <p className="note" data-testid="gal-count">{data.total} resultados</p>
          <div className="gadmin" data-testid="gal-grid">
            {data.items.map((m) => <Card key={m.id} m={m} onPatch={(b) => patch(m, b)} onMove={(d) => move(m, d)} />)}
          </div>
          {data.items.length < data.total && (
            <div style={{ textAlign: "center", marginTop: 16 }}><button className="btn xbtn" onClick={() => setN(n + PAGE)} data-testid="gal-more-btn">Cargar más</button></div>
          )}
        </>
      )}
    </div>
  );
};
